import React, { useMemo } from "react";
import { z } from "zod";
import { SbBlokData } from "@storyblok/js";
import {
  withGlobalStory,
  WithGlobalStoryProps,
} from "../../utils/with-global-story";
import {
  getStoriesFromGlobalStoryblokStoriesByComponentType,
  getStoryFromGlobalStoryblokStoriesBySlug,
} from "../../utils/global-story-functions";
import { TopbarProps } from "@made-people/blueprint-topbar";
import { TopbarTrustpilot } from "./topbar-trustpilot";
import { TopbarPaymentLogos } from "./topbar-payment-logos";
import { getExistingContent } from "./topbar-content";
import { useShippingThreshold } from "../../hooks/use-shipping-threshold";

const topbarStorySchema = z.object({
  settings_content: z.array(z.unknown()).optional(),
});

const sbBlokDataSchema = z.custom<SbBlokData>(
  (val) =>
    typeof val === "object" &&
    val !== null &&
    "_uid" in val &&
    "component" in val,
);

const partnerLogosStorySchema = z.object({
  logos: z
    .array(
      z.object({
        logos: z.array(sbBlokDataSchema).optional(),
      }),
    )
    .optional(),
});

const BaseComponent = withGlobalStory(
  {
    lookupType: "componentType",
    componentType: "Topbar",
  },
  (story) =>
    ({
      settings: { template: "fade" },
      ...story,
    }) satisfies TopbarProps,
);

export const Topbar = (props: WithGlobalStoryProps) => {
  const { thresholdEnabled, threshold } = useShippingThreshold();
  const stories = getStoriesFromGlobalStoryblokStoriesByComponentType(
    "Topbar",
    props.globalStoryblokStories,
  );
  const storyResult = topbarStorySchema.safeParse(stories?.[0]);
  const story = storyResult.success ? storyResult.data : undefined;

  const partnerLogosRaw = getStoryFromGlobalStoryblokStoriesBySlug(
    "shared-content/partner-logos/partner-logos",
    props.globalStoryblokStories,
  );
  const partnerLogosResult = partnerLogosStorySchema.safeParse(partnerLogosRaw);
  const logos = partnerLogosResult.success
    ? (partnerLogosResult.data.logos?.[0]?.logos ?? [])
    : [];

  const customItems: Array<{ customContent: React.ReactNode }> = [];

  customItems.push({
    customContent: <TopbarTrustpilot />,
  });

  if (logos.length > 0) {
    customItems.push({
      customContent: <TopbarPaymentLogos logos={logos} />,
    });
  }
  const existingContent = useMemo(
    () =>
      getExistingContent(story?.settings_content, thresholdEnabled, threshold),
    [story?.settings_content, thresholdEnabled, threshold],
  );

  return (
    <BaseComponent
      {...props}
      additionalProps={{
        settings_content: [...existingContent, ...customItems],
      }}
    />
  );
};
